import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import PageLoader from '../ui/PageLoader';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import PageShell from '../ui/PageShell';
import {
  getPassportFlags,
  getPassportSummary,
  isPassportEnabled,
  listPassportEncounters,
  type PassportEncounter,
  type PassportEncounterFilters,
  type PassportFeatureFlags,
  type PassportSummary,
} from '../../services/api/passport';
import {
  PASSPORT_DISABLED_DESCRIPTION,
  PASSPORT_DISABLED_TITLE,
  SUBJECT_TYPE_LABELS,
  encounterStatusLabel,
  encounterStatusTone,
  formatEncounterDate,
  verificationLevelLabel,
} from './passportCopy';
import PassportScanPanel from './PassportScanPanel';

const STATUS_TONE_CLASS: Record<ReturnType<typeof encounterStatusTone>, string> = {
  emerald: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25',
  amber: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
  red: 'bg-red-500/10 text-red-300 border-red-500/25',
  neutral: 'bg-white/[0.06] text-gray-300 border-white/[0.1]',
};

const PassportPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [flags, setFlags] = useState<PassportFeatureFlags | null>(null);
  const [flagsLoading, setFlagsLoading] = useState(true);
  const [summary, setSummary] = useState<PassportSummary | null>(null);
  const [encounters, setEncounters] = useState<PassportEncounter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<PassportEncounterFilters>({});

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: location.pathname + location.search }, replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setFlagsLoading(true);
      const f = await getPassportFlags();
      if (!cancelled) {
        setFlags(f);
        setFlagsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, encountersResult] = await Promise.all([
        getPassportSummary(),
        listPassportEncounters(filters),
      ]);
      setSummary(summaryData);
      setEncounters(encountersResult.data);
      void apiService.trackEvent('passport_timeline_viewed', {
        metadata: { subject_type: filters.subject_type || 'all' },
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load your Passport');
      setEncounters([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || flagsLoading) return;
    if (!isPassportEnabled(flags)) return;
    void load();
  }, [authLoading, isAuthenticated, flagsLoading, flags, load]);

  if (authLoading || (!isAuthenticated && !error) || flagsLoading) {
    return <PageLoader message="Loading Passport…" />;
  }

  if (!isAuthenticated) {
    return null;
  }

  if (!isPassportEnabled(flags)) {
    return (
      <PageShell contentClassName="!py-6 sm:!py-8">
        <PageHeader
          eyebrow="Bilt Passport"
          title="Your Passport"
          backLink={{ label: 'Back to Garage', to: '/garage' }}
        />
        <EmptyState title={PASSPORT_DISABLED_TITLE} description={PASSPORT_DISABLED_DESCRIPTION} />
      </PageShell>
    );
  }

  return (
    <PageShell contentClassName="!py-6 sm:!py-8">
      <PageHeader
        eyebrow="Bilt Passport"
        title="Your Passport"
        description="A private record of the builds and events you've seen in person. History over score."
        backLink={{ label: 'Back to Garage', to: '/garage' }}
        actions={
          <>
            <Link to={`/passport/recap?year=${new Date().getFullYear()}`} className="btn-secondary btn-sm">
              Year in Passport
            </Link>
            <Link to="/passport/collections" className="btn-secondary btn-sm">
              Collections
            </Link>
            <Link to="/passport/campaigns" className="btn-secondary btn-sm">
              Campaigns
            </Link>
          </>
        }
      />

      {summary ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <div className="stat-tile-accent">
            <p className="text-2xl font-semibold text-white tabular-nums">{summary.encounter_count}</p>
            <p className="text-xs text-gray-400 mt-1">Encounters logged</p>
          </div>
          <div className="stat-tile">
            <p className="text-2xl font-semibold text-white tabular-nums">{summary.vehicles_encountered_count}</p>
            <p className="text-xs text-gray-400 mt-1">Builds seen</p>
          </div>
          <div className="stat-tile">
            <p className="text-2xl font-semibold text-white tabular-nums">{summary.collections_completed}</p>
            <p className="text-xs text-gray-400 mt-1">Collections complete</p>
          </div>
          <div className="stat-tile">
            <p className="text-2xl font-semibold text-white tabular-nums">{summary.campaigns_joined}</p>
            <p className="text-xs text-gray-400 mt-1">Campaigns joined</p>
          </div>
        </div>
      ) : null}

      <div className="mb-6">
        <PassportScanPanel onLogged={() => void load()} />
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-6">
        <label className="sr-only" htmlFor="passport-subject-filter">
          Filter by type
        </label>
        <select
          id="passport-subject-filter"
          value={filters.subject_type || ''}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              subject_type: (e.target.value || undefined) as PassportEncounterFilters['subject_type'],
            }))
          }
          className="input max-w-[180px] min-h-[44px]"
        >
          <option value="">All types</option>
          {Object.entries(SUBJECT_TYPE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
        <label className="sr-only" htmlFor="passport-status-filter">
          Filter by status
        </label>
        <select
          id="passport-status-filter"
          value={filters.status || ''}
          onChange={(e) =>
            setFilters((f) => ({
              ...f,
              status: (e.target.value || undefined) as PassportEncounterFilters['status'],
            }))
          }
          className="input max-w-[180px] min-h-[44px]"
        >
          <option value="">All statuses</option>
          <option value="verified">Verified</option>
          <option value="pending">Pending validation</option>
          <option value="rejected">Not verified</option>
        </select>
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm" role="status">
          Loading your timeline…
        </p>
      ) : error ? (
        <EmptyState
          title="Couldn't load your Passport"
          description={error}
          action={
            <Button type="button" variant="primary" onClick={() => void load()}>
              Try again
            </Button>
          }
        />
      ) : encounters.length === 0 ? (
        <EmptyState
          title="No encounters yet"
          description="Scan a build's QR at a show, shop, or club meet to start your Passport."
        />
      ) : (
        <ul className="space-y-3 list-none">
          {encounters.map((encounter) => {
            const tone = encounterStatusTone(encounter.status);
            return (
              <li key={encounter.public_id}>
                <Link
                  to={`/passport/encounters/${encodeURIComponent(encounter.public_id)}`}
                  className="card-interactive flex items-center justify-between gap-4 p-4"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {encounter.subject?.title || SUBJECT_TYPE_LABELS[encounter.subject_type]}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {SUBJECT_TYPE_LABELS[encounter.subject_type]} · {formatEncounterDate(encounter.occurred_at)} ·{' '}
                      {verificationLevelLabel(encounter.verification_level)}
                    </p>
                  </div>
                  <span
                    className={`badge shrink-0 border text-xs ${STATUS_TONE_CLASS[tone]}`}
                    aria-label={`Status: ${encounterStatusLabel(encounter.status)}`}
                  >
                    {encounterStatusLabel(encounter.status)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </PageShell>
  );
};

export default PassportPage;
